import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, FileText, Receipt, Users,
  ChevronRight, CheckCircle2, Truck, ArrowRight, BookOpenCheck
} from 'lucide-react';

const steps = [
  {
    id: 1,
    title: 'Welcome to Transsolution',
    description: 'Your complete logistics billing platform.',
    icon: Truck,
    color: 'bg-orange-100 text-orange-600',
    content: 'Transsolution is a white-label logistics accounting ERP for transport and logistics companies. It supports:\n\n- Double-Entry Accounting (Busy-style vouchers)\n- Loading Receipts — Shipment documents\n- Tax Invoices — Full GST-compliant invoices\n- P&L Statement, Trial Balance, Balance Sheet\n- Client Analytics, GST Reports, Audit Logs\n- White-label customization per company',
  },
  {
    id: 2,
    title: 'Dashboard',
    description: 'View key metrics, charts, and recent activity.',
    icon: LayoutDashboard,
    color: 'bg-emerald-100 text-emerald-600',
    content: 'The dashboard shows:\n- Total receipts, invoices, revenue, pending & overdue counts\n- GST Payable, TDS Payable, TDS Receivable, Unadjusted Advances\n- Monthly revenue bar chart\n- Invoice status pie chart\n- Receivables & Payables summary (Admin only)\n- Employee activity tracking (Admin only)',
    nav: '/',
  },
  {
    id: 3,
    title: 'Loading Receipts',
    description: 'Create shipment documents for consignments.',
    icon: Receipt,
    color: 'bg-blue-100 text-blue-600',
    content: 'Loading Receipts are shipment documents with no financial calculations. They capture:\n- Consignor & consignee details\n- Origin/destination with states\n- Invoice & e-way bill details\n- Package dimensions & weights\n- Product details\n\nEach receipt gets an auto-generated number and can be downloaded as a PDF.',
    nav: '/receipts',
  },
  {
    id: 4,
    title: 'Tax Invoices',
    description: 'Generate GST-compliant invoices with auto calculation.',
    icon: FileText,
    color: 'bg-violet-100 text-violet-600',
    content: 'Tax Invoices include full financial calculations:\n- Line items with HSN/SAC codes, quantity, pricing\n- Auto GST (CGST+SGST or IGST based on Place of Supply)\n- TDS deduction with section tracking\n- Advance adjustment (FIFO)\n- Round-off and net payable\n\nInvoice status workflow: Draft > Pending > Paid/Overdue',
    nav: '/invoices',
  },
  {
    id: 5,
    title: 'Employee Management',
    description: 'Admin: Create and manage employee accounts.',
    icon: Users,
    color: 'bg-rose-100 text-rose-600',
    content: 'Admins can create employee accounts with username/email/password. Two roles:\n\n- Admin: Full access — manage employees, ledger, audit logs, GST reports, delete documents\n- Employee: Create/edit receipts and invoices, view and download PDFs\n\nAll actions are tracked with the employee\'s name for accountability.',
    nav: '/employees',
    adminOnly: true,
  },
  {
    id: 6,
    title: 'Accounting & Reports',
    description: 'Admin: Full accounting suite and compliance reports.',
    icon: BookOpenCheck,
    color: 'bg-amber-100 text-amber-600',
    content: 'Admin-only features:\n\n- Unified Transactions: All invoices, advances, receipts in one view\n- Journal Entries, Chart of Accounts, Ledger\n- Trial Balance, Profit & Loss, Balance Sheet\n- GST Returns (GSTR-1, GSTR-3B) with JSON export\n- TDS Returns (26Q-ready) with section/party breakdown\n- Party Analytics and Advance Summary',
    nav: '/transactions',
    adminOnly: true,
  },
];

export default function Tutorial() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(new Set());

  const visibleSteps = steps.filter(s => !s.adminOnly || isAdmin);
  const step = visibleSteps[currentStep];

  return (
    <div data-testid="tutorial-page" className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Getting Started Guide</h1>
        <p className="text-sm text-slate-500 font-body mt-1">Learn how to use Transsolution</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="space-y-1">
          {visibleSteps.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setCurrentStep(i)}
              data-testid={`tutorial-step-${s.id}`}
              className={`w-full text-left px-4 py-3 rounded-md flex items-center gap-3 transition-all duration-200 ${
                i === currentStep ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-600'
              }`}
            >
              {completed.has(s.id) ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={2} />
              ) : (
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  i === currentStep ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'
                }`}>{i + 1}</span>
              )}
              <span className="text-sm font-medium truncate">{s.title}</span>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-12 h-12 rounded-md ${step.color} flex items-center justify-center`}>
              <step.icon className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-slate-900">{step.title}</h2>
              <p className="text-sm text-slate-500">{step.description}</p>
            </div>
          </div>

          <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line mb-6">
            {step.content}
          </div>

          <div className="flex items-center gap-3">
            {step.nav && (
              <button onClick={() => { navigate(step.nav); setCompleted(c => new Set([...c, step.id])); }} className="btn-secondary flex items-center gap-2" data-testid="tutorial-nav-btn">
                Go to {step.title} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
            {currentStep < visibleSteps.length - 1 && (
              <button onClick={() => { setCompleted(c => new Set([...c, step.id])); setCurrentStep(currentStep + 1); }} className="btn-primary flex items-center gap-2" data-testid="tutorial-next-btn">
                Next <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
            {currentStep === visibleSteps.length - 1 && (
              <button onClick={() => navigate('/')} className="btn-accent flex items-center gap-2" data-testid="tutorial-finish-btn">
                Start Using Transsolution <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
